import { User } from 'fitify-types/src/types/user'
import type { ImageProps } from 'fitify-ui-onboarding/src/types/ui'

import type { FunnelsWithRoute } from 'types/routes'
import { getImageProps } from 'utils/images'

import { Funnel, Question } from '../../../types/state'

export enum BodyFat {
  Low,
  Medium,
  High,
  VeryHigh,
}

export enum FitnessLevel {
  Low = 1,
  Medium = 2,
  High = 3,
}

export type GoalComparisonFunnel = FunnelsWithRoute<'subscription'>

export type GetGoalImageArgs = {
  bodyGoal?: Question.BodyGoal
  funnel?: GoalComparisonFunnel
  variant?: string
}

export const getGoalImage = ({
  bodyGoal,
  funnel,
  variant,
}: GetGoalImageArgs): ImageProps => {
  switch (funnel) {
    case Funnel.CHF01: {
      switch (bodyGoal) {
        case Question.BodyGoal.Thin:
          return getImageProps(
            Funnel.CHF01,
            'subscription',
            'realisticAfterThin'
          )
        case Question.BodyGoal.Toned:
          return getImageProps(
            Funnel.CHF01,
            'subscription',
            'realisticAfterToned'
          )
        case Question.BodyGoal.Curvy:
          return getImageProps(
            Funnel.CHF01,
            'subscription',
            'realisticAfterCurvy'
          )
        case Question.BodyGoal.Average:
        default:
          return getImageProps(
            Funnel.CHF01,
            'subscription',
            'realisticAfterAverage'
          )
      }
    }
    case Funnel.TCHF01: {
      switch (bodyGoal) {
        case Question.BodyGoal.Thin:
          return getImageProps(
            Funnel.TCHF01,
            'subscription',
            'realisticAfterThin'
          )
        case Question.BodyGoal.Toned:
          return getImageProps(
            Funnel.TCHF01,
            'subscription',
            'realisticAfterToned'
          )
        case Question.BodyGoal.Curvy:
          return getImageProps(
            Funnel.TCHF01,
            'subscription',
            'realisticAfterCurvy'
          )
        case Question.BodyGoal.Average:
        default:
          return getImageProps(
            Funnel.TCHF01,
            'subscription',
            'realisticAfterAverage'
          )
      }
    }
    case Funnel.CHM01: {
      switch (bodyGoal) {
        case Question.BodyGoal.BitLeaner:
          return getImageProps(
            Funnel.CHM01,
            'subscription',
            'realisticAfterBitLeaner'
          )
        case Question.BodyGoal.Athletic:
          return getImageProps(
            Funnel.CHM01,
            'subscription',
            'realisticAfterAthletic'
          )
        case Question.BodyGoal.Shredded:
          return getImageProps(
            Funnel.CHM01,
            'subscription',
            'realisticAfterShredded'
          )
        case Question.BodyGoal.ExtraBulky:
        default:
          return getImageProps(
            Funnel.CHM01,
            'subscription',
            'realisticAfterExtraBulky'
          )
      }
    }
    case Funnel.TCHM01: {
      switch (bodyGoal) {
        case Question.BodyGoal.BitLeaner:
          return getImageProps(
            Funnel.TCHM01,
            'subscription',
            'realisticAfterBitLeaner'
          )
        case Question.BodyGoal.Athletic:
          return getImageProps(
            Funnel.TCHM01,
            'subscription',
            'realisticAfterAthletic'
          )
        case Question.BodyGoal.Shredded:
          return getImageProps(
            Funnel.TCHM01,
            'subscription',
            'realisticAfterShredded'
          )
        case Question.BodyGoal.ExtraBulky:
        default:
          return getImageProps(
            Funnel.TCHM01,
            'subscription',
            'realisticAfterExtraBulky'
          )
      }
    }
    case Funnel.M01: {
      switch (bodyGoal) {
        case Question.BodyGoal.BitLeaner:
          return getImageProps(Funnel.M01, 'subscription', 'afterBitLeaner', {
            variant,
          })
        case Question.BodyGoal.Athletic:
          return getImageProps(Funnel.M01, 'subscription', 'afterAthletic', {
            variant,
          })
        case Question.BodyGoal.Shredded:
          return getImageProps(Funnel.M01, 'subscription', 'afterShredded', {
            variant,
          })
        case Question.BodyGoal.ExtraBulky:
        default:
          return getImageProps(Funnel.M01, 'subscription', 'afterExtraBulky', {
            variant,
          })
      }
    }
    case Funnel.MILICAM01: {
      switch (bodyGoal) {
        case Question.BodyGoal.BitLeaner:
          return getImageProps(
            Funnel.MILICAM01,
            'subscription',
            'afterBitLeaner',
            { variant }
          )
        case Question.BodyGoal.Athletic:
          return getImageProps(
            Funnel.MILICAM01,
            'subscription',
            'afterAthletic',
            { variant }
          )
        case Question.BodyGoal.Shredded:
          return getImageProps(
            Funnel.MILICAM01,
            'subscription',
            'afterShredded',
            { variant }
          )
        case Question.BodyGoal.ExtraBulky:
        default:
          return getImageProps(
            Funnel.MILICAM01,
            'subscription',
            'afterExtraBulky',
            { variant }
          )
      }
    }
    case Funnel.HOPIF01:
    case Funnel.MILICAF01:
    case Funnel.WALLPIF01:
    case Funnel.F01:
    case Funnel.GYMF01:
    default: {
      const resolvedFunnel = funnel || Funnel.F01
      switch (bodyGoal) {
        case Question.BodyGoal.Thin:
          return getImageProps(resolvedFunnel, 'subscription', 'afterThin', {
            variant,
          })
        case Question.BodyGoal.Toned:
          return getImageProps(resolvedFunnel, 'subscription', 'afterToned', {
            variant,
          })
        case Question.BodyGoal.Curvy:
          return getImageProps(resolvedFunnel, 'subscription', 'afterCurvy', {
            variant,
          })
        case Question.BodyGoal.Average:
        default:
          return getImageProps(resolvedFunnel, 'subscription', 'afterAverage', {
            variant,
          })
      }
    }
  }
}

export type GetNowImageArgs = {
  funnel?: GoalComparisonFunnel
  nowBodyFat: BodyFat
  variant?: string
}

export const getNowImage = ({
  funnel,
  nowBodyFat,
  variant,
}: GetNowImageArgs): ImageProps => {
  switch (funnel) {
    case Funnel.CHF01: {
      switch (nowBodyFat) {
        case BodyFat.Low:
          return getImageProps(
            Funnel.CHF01,
            'subscription',
            'realisticBeforeSlim'
          )
        case BodyFat.Medium:
          return getImageProps(
            Funnel.CHF01,
            'subscription',
            'realisticBeforeMidsized'
          )
        case BodyFat.High:
          return getImageProps(
            Funnel.CHF01,
            'subscription',
            'realisticBeforeFullfigured'
          )
        case BodyFat.VeryHigh:
        default:
          return getImageProps(
            Funnel.CHF01,
            'subscription',
            'realisticBeforeExtended'
          )
      }
    }
    case Funnel.TCHF01: {
      switch (nowBodyFat) {
        case BodyFat.Low:
          return getImageProps(
            Funnel.TCHF01,
            'subscription',
            'realisticBeforeSlim'
          )
        case BodyFat.Medium:
          return getImageProps(
            Funnel.TCHF01,
            'subscription',
            'realisticBeforeMidsized'
          )
        case BodyFat.High:
          return getImageProps(
            Funnel.TCHF01,
            'subscription',
            'realisticBeforeFullfigured'
          )
        case BodyFat.VeryHigh:
        default:
          return getImageProps(
            Funnel.TCHF01,
            'subscription',
            'realisticBeforeExtended'
          )
      }
    }
    case Funnel.CHM01: {
      switch (nowBodyFat) {
        case BodyFat.Low:
          return getImageProps(
            Funnel.CHM01,
            'subscription',
            'realisticBeforeSlender'
          )
        case BodyFat.Medium:
          return getImageProps(
            Funnel.CHM01,
            'subscription',
            'realisticBeforeMediumBuild'
          )
        case BodyFat.High:
          return getImageProps(
            Funnel.CHM01,
            'subscription',
            'realisticBeforeBigger'
          )
        case BodyFat.VeryHigh:
        default:
          return getImageProps(
            Funnel.CHM01,
            'subscription',
            'realisticBeforeSignificantlyOverweight'
          )
      }
    }
    case Funnel.TCHM01: {
      switch (nowBodyFat) {
        case BodyFat.Low:
          return getImageProps(
            Funnel.TCHM01,
            'subscription',
            'realisticBeforeSlender'
          )
        case BodyFat.Medium:
          return getImageProps(
            Funnel.TCHM01,
            'subscription',
            'realisticBeforeMediumBuild'
          )
        case BodyFat.High:
          return getImageProps(
            Funnel.TCHM01,
            'subscription',
            'realisticBeforeBigger'
          )
        case BodyFat.VeryHigh:
        default:
          return getImageProps(
            Funnel.TCHM01,
            'subscription',
            'realisticBeforeSignificantlyOverweight'
          )
      }
    }
    case Funnel.M01: {
      switch (nowBodyFat) {
        case BodyFat.Low:
          return getImageProps(Funnel.M01, 'subscription', 'beforeSlender', {
            variant,
          })
        case BodyFat.Medium:
          return getImageProps(
            Funnel.M01,
            'subscription',
            'beforeMediumBuild',
            { variant }
          )
        case BodyFat.High:
          return getImageProps(Funnel.M01, 'subscription', 'beforeBigger', {
            variant,
          })
        case BodyFat.VeryHigh:
        default:
          return getImageProps(
            Funnel.M01,
            'subscription',
            'beforeSignificantlyOverweight',
            { variant }
          )
      }
    }
    case Funnel.MILICAM01: {
      switch (nowBodyFat) {
        case BodyFat.Low:
          return getImageProps(
            Funnel.MILICAM01,
            'subscription',
            'beforeSlender',
            { variant }
          )
        case BodyFat.Medium:
          return getImageProps(
            Funnel.MILICAM01,
            'subscription',
            'beforeMediumBuild',
            { variant }
          )
        case BodyFat.High:
          return getImageProps(
            Funnel.MILICAM01,
            'subscription',
            'beforeBigger',
            { variant }
          )
        case BodyFat.VeryHigh:
        default:
          return getImageProps(
            Funnel.MILICAM01,
            'subscription',
            'beforeSignificantlyOverweight',
            { variant }
          )
      }
    }
    case Funnel.HOPIF01:
    case Funnel.MILICAF01:
    case Funnel.WALLPIF01:
    case Funnel.F01:
    case Funnel.GYMF01:
    default: {
      const resolvedFunnel = funnel || Funnel.F01
      switch (nowBodyFat) {
        case BodyFat.Low:
          return getImageProps(resolvedFunnel, 'subscription', 'beforeSlim', {
            variant,
          })
        case BodyFat.Medium:
          return getImageProps(
            resolvedFunnel,
            'subscription',
            'beforeMidsized',
            {
              variant,
            }
          )
        case BodyFat.High:
          return getImageProps(
            resolvedFunnel,
            'subscription',
            'beforeFullfigured',
            {
              variant,
            }
          )
        case BodyFat.VeryHigh:
        default:
          return getImageProps(
            resolvedFunnel,
            'subscription',
            'beforeExtended',
            {
              variant,
            }
          )
      }
    }
  }
}

export type GetNowBodyFatArgs = Pick<User.Profile, 'current_shape'>

export const getNowBodyFat = ({
  current_shape,
}: GetNowBodyFatArgs): BodyFat => {
  switch (current_shape) {
    case User.Profile.CurrentShape.SkinnySlim:
      return BodyFat.Low
    case User.Profile.CurrentShape.Average:
      return BodyFat.Medium
    case User.Profile.CurrentShape.PlusSize:
      return BodyFat.High
    case User.Profile.CurrentShape.Extra:
    default:
      return BodyFat.VeryHigh
  }
}

export type GetGoalBodyFatArgs = Pick<User.Profile, 'gender'> & {
  bodyGoal?: Question.BodyGoal
}

export const getGoalBodyFat = ({
  gender,
  bodyGoal,
}: GetGoalBodyFatArgs): BodyFat => {
  if (gender === User.Profile.Gender.Male) {
    switch (bodyGoal) {
      case Question.BodyGoal.BitLeaner:
        return BodyFat.Medium
      case Question.BodyGoal.Athletic:
        return BodyFat.Low
      case Question.BodyGoal.Shredded:
        return BodyFat.Low
      case Question.BodyGoal.ExtraBulky:
      default:
        return BodyFat.Medium
    }
  } else {
    switch (bodyGoal) {
      case Question.BodyGoal.Thin:
        return BodyFat.Low
      case Question.BodyGoal.Toned:
        return BodyFat.Low
      case Question.BodyGoal.Curvy:
        return BodyFat.Medium
      case Question.BodyGoal.Average:
      default:
        return BodyFat.Medium
    }
  }
}

export type GetNowFitnessLevelArgs = {
  how_often_exercise?: Question.HowOftenExercise
}

export const getNowFitnessLevel = ({
  how_often_exercise,
}: GetNowFitnessLevelArgs): FitnessLevel => {
  switch (how_often_exercise) {
    case Question.HowOftenExercise.Daily:
    case Question.HowOftenExercise.Weekly:
      return FitnessLevel.Medium
    case Question.HowOftenExercise.Monthly:
    case Question.HowOftenExercise.Never:
    default:
      return FitnessLevel.Low
  }
}
